import React, { useState, useEffect } from 'react';
import { 
  StyleSheet, 
  View, 
  Text, 
  FlatList,
  TouchableOpacity, 
  Alert, 
  ActivityIndicator, 
  Modal,
  Image,
  RefreshControl,
  Dimensions,
  TextInput
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, arrayUnion, arrayRemove } from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { User } from '../../types';
import { colors, typography, spacing, borderRadius } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useTheme } from '../../context/ThemeContext';
import { useFriendRequests } from '../../hooks/useFriendRequests';
import { createFriendRequestNotification, createFriendAcceptedNotification } from '../../services/notificationService';
import { useNotificationsListener } from '../../hooks/useNotificationsListener';
import { showAlert } from '../../utils/alert';

type Props = NativeStackScreenProps<MainStackParamList, 'FriendManagement'>;

interface FriendUser {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  profilePhoto?: string;
  status?: 'friend' | 'pending' | 'requested';
}

interface FriendRequest {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  username: string;
  profilePhoto?: string;
  type: 'incoming' | 'outgoing';
}

const FriendManagementScreen = ({ navigation }: Props) => {
  const [friends, setFriends] = useState<FriendUser[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [activeTab, setActiveTab] = useState<'friends' | 'requests'>('friends');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FriendUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [friendsLoading, setFriendsLoading] = useState(true);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [refreshingFriends, setRefreshingFriends] = useState(false);
  const [refreshingRequests, setRefreshingRequests] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [friendCode, setFriendCode] = useState('');
  const { theme } = useTheme();
  const { resetNotificationCount } = useFriendRequests(true);
  const { markFriendRequestNotificationsAsRead } = useNotificationsListener();
  
  // Detectar si estamos en una pantalla pequeña
  const { width } = Dimensions.get('window');
  const isSmallScreen = width < 375; // iPhone SE o similar

  useEffect(() => {
    fetchFriends();
    fetchFriendRequests();
    markFriendRequestNotificationsAsRead();
  }, []);

  const fetchFriends = async () => {
    setFriendsLoading(true);
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Get user document to get friends list
      const userRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }
      
      const userData = userDoc.data() as User;
      const friendIds = userData.friends || [];
      
      if (friendIds.length === 0) {
        setFriends([]);
        setFriendsLoading(false);
        return;
      }
      
      // Fetch friend data
      const friendsData: FriendUser[] = [];
      
      // Fetch friends in parallel
      await Promise.all(
        friendIds.map(async (friendId) => {
          const friendRef = doc(firestore, 'users', friendId);
          const friendDoc = await getDoc(friendRef);
          
          if (friendDoc.exists()) {
            const friendData = friendDoc.data() as User;
            friendsData.push({
              id: friendId,
              firstName: friendData.firstName,
              lastName: friendData.lastName,
              username: friendData.username,
              profilePhoto: friendData.profilePhoto,
              status: 'friend'
            });
          }
        })
      );
      
      setFriends(friendsData);
    } catch (error) {
      console.error('Error al obtener amigos:', error);
      showAlert('Error', 'No se pudieron cargar tus amigos');
    } finally {
      setFriendsLoading(false);
    }
  };

  const fetchFriendRequests = async () => {
    setRequestsLoading(true);
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Get user document
      const userRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }
      
      const userData = userDoc.data() as User;
      
      // Get pending requests (outgoing)
      const pendingIds = userData.pendingFriendRequests || [];
      
      // Get friend requests (incoming)
      const requestIds = userData.friendRequests || [];
      
      const requests: FriendRequest[] = [];
      
      // Fetch all requests in parallel
      await Promise.all([
        // Fetch outgoing requests
        ...pendingIds.map(async (pendingId) => {
          const pendingRef = doc(firestore, 'users', pendingId);
          const pendingDoc = await getDoc(pendingRef);
          
          if (pendingDoc.exists()) {
            const pendingData = pendingDoc.data() as User;
            requests.push({
              id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${pendingId}`,
              userId: pendingId,
              firstName: pendingData.firstName,
              lastName: pendingData.lastName,
              username: pendingData.username,
              profilePhoto: pendingData.profilePhoto,
              type: 'outgoing'
            });
          }
        }),
        
        // Fetch incoming requests
        ...requestIds.map(async (requestId) => {
          const requestRef = doc(firestore, 'users', requestId);
          const requestDoc = await getDoc(requestRef);
          
          if (requestDoc.exists()) {
            const requestData = requestDoc.data() as User;
            requests.push({
              id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${requestId}`,
              userId: requestId,
              firstName: requestData.firstName,
              lastName: requestData.lastName,
              username: requestData.username,
              profilePhoto: requestData.profilePhoto,
              type: 'incoming'
            });
          }
        })
      ]);
      
      setFriendRequests(requests);
    } catch (error) {
      console.error('Error al obtener solicitudes de amistad:', error);
      showAlert('Error', 'No se pudieron cargar las solicitudes de amistad');
    } finally {
      setRequestsLoading(false);
    }
  };

  const searchUsersByUsername = async () => {
    if (!searchQuery.trim()) {
      showAlert('Error', 'Por favor ingresa un nombre de usuario');
      return;
    }
    
    setSearchLoading(true);
    setSearchResults([]);
    
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Get current user data
      const userRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }
      
      const userData = userDoc.data() as User;
      const currentFriends = userData.friends || [];
      const pendingRequests = userData.pendingFriendRequests || [];
      const friendRequests = userData.friendRequests || [];
      
      // Search for users
      const usersRef = collection(firestore, 'users');
      const q = query(
        usersRef, 
        where('username', '>=', searchQuery),
        where('username', '<=', searchQuery + '\uf8ff')
      );
      
      const querySnapshot = await getDocs(q);
      const results: FriendUser[] = [];
      
      querySnapshot.forEach((doc) => {
        // Skip current user
        if (doc.id === userId) return;
        
        const userData = doc.data() as User;
        
        let status: FriendUser['status'] = undefined;
        
        // Check if already friend
        if (currentFriends.includes(doc.id)) {
          status = 'friend';
        } 
        // Check if pending request
        else if (pendingRequests.includes(doc.id)) {
          status = 'pending';
        } 
        // Check if request received
        else if (friendRequests.includes(doc.id)) {
          status = 'requested';
        }
        
        results.push({
          id: doc.id,
          firstName: userData.firstName,
          lastName: userData.lastName,
          username: userData.username,
          profilePhoto: userData.profilePhoto,
          status
        });
      });
      
      setSearchResults(results);
    } catch (error) {
      console.error('Error al buscar usuarios:', error);
      showAlert('Error', 'No se pudieron buscar usuarios');
    } finally {
      setSearchLoading(false);
    }
  };

  const searchUserByFriendCode = async () => {
    if (!friendCode.trim()) {
      showAlert('Error', 'Por favor ingresa un código de amigo');
      return;
    }
    
    setSearchLoading(true);
    
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Get current user data
      const userRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }
      
      const userData = userDoc.data() as User;
      const currentFriends = userData.friends || [];
      const pendingRequests = userData.pendingFriendRequests || [];
      const friendRequests = userData.friendRequests || [];
      
      // Search by friend code
      const usersRef = collection(firestore, 'users');
      const q = query(usersRef, where('friendCode', '==', friendCode.trim().toUpperCase()));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        showAlert('Error', 'No se encontró ningún usuario con ese código');
        setSearchLoading(false);
        return;
      }
      
      const results: FriendUser[] = [];
      
      querySnapshot.forEach((doc) => {
        // Skip current user
        if (doc.id === userId) return;
        
        const userData = doc.data() as User;
        
        let status: FriendUser['status'] = undefined;
        
        // Check if already friend
        if (currentFriends.includes(doc.id)) {
          status = 'friend';
        } 
        // Check if pending request
        else if (pendingRequests.includes(doc.id)) {
          status = 'pending';
        } 
        // Check if request received
        else if (friendRequests.includes(doc.id)) {
          status = 'requested';
        }
        
        results.push({
          id: doc.id,
          firstName: userData.firstName,
          lastName: userData.lastName,
          username: userData.username,
          profilePhoto: userData.profilePhoto,
          status
        });
      });
      
      setSearchResults(results);
    } catch (error) {
      console.error('Error al buscar usuarios por código:', error);
      showAlert('Error', 'No se pudo buscar usuario por código');
    } finally {
      setSearchLoading(false);
    }
  };

  const sendFriendRequest = async (friendId: string) => {
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Get current user
      const userRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('No se encontró el perfil de usuario');
      }
      
      const userData = userDoc.data() as User;
      
      // Check if already friends
      const currentFriends = userData.friends || [];
      if (currentFriends.includes(friendId)) {
        showAlert('Información', 'Ya eres amigo de este usuario');
        return;
      }
      
      // Check if already sent request
      const pendingRequests = userData.pendingFriendRequests || [];
      if (pendingRequests.includes(friendId)) {
        showAlert('Información', 'Ya has enviado una solicitud a este usuario');
        return;
      }
      
      // Get friend user
      const friendRef = doc(firestore, 'users', friendId);
      const friendDoc = await getDoc(friendRef);
      
      if (!friendDoc.exists()) {
        throw new Error('No se encontró el perfil del usuario destino');
      }
      
      // Add to pending
      await updateDoc(userRef, {
        pendingFriendRequests: arrayUnion(friendId)
      });
      
      // Add to friend requests
      await updateDoc(friendRef, {
        friendRequests: arrayUnion(userId)
      });
      
      // Refresh requests
      fetchFriendRequests();
      
      // Close search modal
      setSearchModalVisible(false);
      setSearchQuery('');
      setFriendCode('');
      setSearchResults([]);
      
      showAlert('Éxito', 'Solicitud de amistad enviada');

      // Crear notificación de solicitud de amistad
      await createFriendRequestNotification(friendId, userId);
    } catch (error) {
      console.error('Error al enviar solicitud de amistad:', error);
      showAlert('Error', 'No se pudo enviar la solicitud de amistad');
    }
  };

  const acceptFriendRequest = async (friendId: string) => {
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Get current user
      const userRef = doc(firestore, 'users', userId);
      const friendRef = doc(firestore, 'users', friendId);
      
      // Update both users' documents
      await Promise.all([
        // Add to current user's friends and remove from requests
        updateDoc(userRef, {
          friends: arrayUnion(friendId),
          friendRequests: arrayRemove(friendId)
        }),
        
        // Add current user to friend's friends and remove from pending
        updateDoc(friendRef, {
          friends: arrayUnion(userId),
          pendingFriendRequests: arrayRemove(userId)
        })
      ]);
      
      // Refresh data
      fetchFriends();
      fetchFriendRequests();
      
      showAlert('Éxito', 'Solicitud de amistad aceptada');

      // Crear notificación de aceptación de amistad
      await createFriendAcceptedNotification(userId, friendId);
    } catch (error) {
      console.error('Error al aceptar solicitud de amistad:', error);
      showAlert('Error', 'No se pudo aceptar la solicitud de amistad');
    }
  };

  const rejectFriendRequest = async (friendId: string) => {
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Get current user
      const userRef = doc(firestore, 'users', userId);
      const friendRef = doc(firestore, 'users', friendId);
      
      // Update both users' documents
      await Promise.all([
        // Remove from current user's requests
        updateDoc(userRef, {
          friendRequests: arrayRemove(friendId)
        }),
        
        // Remove current user from friend's pending
        updateDoc(friendRef, {
          pendingFriendRequests: arrayRemove(userId)
        })
      ]);
      
      // Marcar notificación como leída
      // Buscar y marcar como leída la notificación relacionada
      const notificationsRef = collection(firestore, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('type', '==', 'friend_request'),
        where('relatedId', '==', friendId)
      );
      
      const querySnapshot = await getDocs(q);
      
      // Actualizar cada notificación encontrada
      const updatePromises = querySnapshot.docs.map((notificationDoc) => {
        return updateDoc(doc(firestore, 'notifications', notificationDoc.id), {
          read: true,
          displayed: true
        });
      });
      
      await Promise.all(updatePromises);
      
      // Refresh data
      fetchFriendRequests();
      
      showAlert('Éxito', 'Solicitud de amistad rechazada');
    } catch (error) {
      console.error('Error al rechazar solicitud de amistad:', error);
      showAlert('Error', 'No se pudo rechazar la solicitud de amistad');
    }
  };

  const cancelFriendRequest = async (friendId: string) => {
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Get current user
      const userRef = doc(firestore, 'users', userId);
      const friendRef = doc(firestore, 'users', friendId);
      
      // Update both users' documents
      await Promise.all([
        // Remove from current user's pending
        updateDoc(userRef, {
          pendingFriendRequests: arrayRemove(friendId)
        }),
        
        // Remove current user from friend's requests
        updateDoc(friendRef, {
          friendRequests: arrayRemove(userId)
        })
      ]);

      // Marcar notificación como leída
      // Buscar y marcar como leída la notificación relacionada
      const notificationsRef = collection(firestore, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', friendId),
        where('type', '==', 'friend_request'),
        where('relatedId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      
      // Actualizar cada notificación encontrada
      const updatePromises = querySnapshot.docs.map((notificationDoc) => {
        return updateDoc(doc(firestore, 'notifications', notificationDoc.id), {
          read: true,
          displayed: true
        });
      });
      
      await Promise.all(updatePromises);
      
      // Refresh data
      fetchFriendRequests();
      
      showAlert('Éxito', 'Solicitud de amistad cancelada');
    } catch (error) {
      console.error('Error al cancelar solicitud de amistad:', error);
      showAlert('Error', 'No se pudo cancelar la solicitud de amistad');
    }
  };

  const removeFriend = async (friendId: string) => {
    try {
      const userId = auth.currentUser?.uid;
      
      if (!userId) {
        throw new Error('Usuario no autenticado');
      }
      
      // Update current user document
      const userRef = doc(firestore, 'users', userId);
      await updateDoc(userRef, {
        friends: arrayRemove(friendId)
      });
      
      // Update friend's document
      const friendRef = doc(firestore, 'users', friendId);
      await updateDoc(friendRef, {
        friends: arrayRemove(userId)
      });
      
      // Update local state
      setFriends(friends.filter(friend => friend.id !== friendId));
      
      showAlert('Éxito', 'Amigo eliminado correctamente');
    } catch (error) {
      console.error('Error al eliminar amigo:', error);
      showAlert('Error', 'No se pudo eliminar el amigo');
    }
  };

  const confirmRemoveFriend = (friend: FriendUser) => {
    showAlert(
      'Confirmar',
      `¿Estás seguro de eliminar a ${friend.firstName} de tus amigos?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        { text: 'Eliminar', onPress: () => removeFriend(friend.id), style: 'destructive' }
      ]
    );
  };

  const renderFriendItem = ({ item }: { item: FriendUser }) => {
    return (
      <Card style={styles.friendItem}>
        <View style={styles.friendInfo}>
          <View style={styles.friendImageContainer}>
            {item.profilePhoto ? (
              <Image source={{ uri: item.profilePhoto }} style={styles.friendImage} />
            ) : (
              <View style={[styles.friendImagePlaceholder, { backgroundColor: theme.colors.primary }]}>
                <Text style={styles.friendInitials}>
                  {item.firstName.charAt(0)}{item.lastName.charAt(0)}
                </Text>
              </View>
            )}
          </View>
          
          <View style={styles.friendDetails}>
            <Text style={[styles.friendName, { color: theme.colors.text }]}>
              {item.firstName} {item.lastName}
            </Text>
            <Text style={[styles.friendUsername, { color: theme.colors.grey.dark }]}>
              @{item.username}
            </Text>
          </View>
        </View>
        
        <View style={styles.friendActions}>
          <TouchableOpacity
            style={[styles.friendAction, { marginRight: spacing.md }]}
            onPress={() => navigation.navigate('FriendProfile', { friendId: item.id })}
          >
            <Ionicons name="person" size={20} color={theme.colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.friendAction}
            onPress={() => confirmRemoveFriend(item)}
          >
            <Ionicons name="person-remove" size={20} color={theme.colors.error} />
          </TouchableOpacity>
        </View>
      </Card>
    );
  };

  const renderSearchResultItem = ({ item }: { item: FriendUser }) => {
    let buttonTitle = "Agregar";
    let buttonType: "primary" | "outline" | "secondary" = "primary";
    let disabled = false;
    let onPressAction = () => sendFriendRequest(item.id);
    
    if (item.status === 'friend') {
      buttonTitle = "Amigo";
      buttonType = "outline";
      disabled = true;
    } else if (item.status === 'pending') {
      buttonTitle = "Pendiente";
      buttonType = "secondary";
      disabled = true;
    } else if (item.status === 'requested') {
      buttonTitle = "Aceptar";
      buttonType = "primary";
      onPressAction = () => acceptFriendRequest(item.id);
    }
    
    return (
      <Card style={styles.searchResultCard}>
        <View style={styles.friendHeader}>
          <View style={styles.friendInfo}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>
                {item.firstName.charAt(0)}{item.lastName.charAt(0)}
              </Text>
            </View>
            
            <View>
              <Text style={[styles.friendName, { color: theme.colors.text }]}>{item.firstName} {item.lastName}</Text>
              <Text style={[styles.friendUsername, { color: theme.colors.grey.dark }]}>@{item.username}</Text>
              {item.status === 'requested' && (
                <Text style={[styles.requestStatusText, { color: theme.colors.grey.dark }]}>
                  Te ha enviado una solicitud
                </Text>
              )}
            </View>
          </View>
          
          <Button
            title={buttonTitle}
            onPress={onPressAction}
            type={buttonType}
            size="small"
            disabled={disabled}
          />
        </View>
      </Card>
    );
  };

  const renderFriendRequestItem = ({ item }: { item: FriendRequest }) => {
    const isIncoming = item.type === 'incoming';

    return (
      <Card style={styles.friendCard}>
        <View style={styles.friendHeader}>
          <View style={styles.friendInfo}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>
                {item.firstName.charAt(0)}{item.lastName.charAt(0)}
              </Text>
            </View>
            
            <View>
              <Text style={[styles.friendName, { color: theme.colors.text }]}>{item.firstName} {item.lastName}</Text>
              <Text style={[styles.friendUsername, { color: theme.colors.grey.dark }]}>@{item.username}</Text>
              <Text style={[styles.requestStatusText, { color: theme.colors.grey.dark }]}>
                {isIncoming ? 'Solicitud recibida' : 'Solicitud enviada'}
              </Text>
            </View>
          </View>
          
          {isIncoming ? (
            <View style={styles.requestActions}>
              <TouchableOpacity
                style={[styles.requestButton, styles.acceptButton]}
                onPress={() => acceptFriendRequest(item.userId)}
              >
                <Ionicons name="checkmark" size={18} color={theme.colors.white} />
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.requestButton, styles.rejectButton]}
                onPress={() => rejectFriendRequest(item.userId)}
              >
                <Ionicons name="close" size={18} color={theme.colors.white} />
              </TouchableOpacity>
            </View>
          ) : (
            <TouchableOpacity
              style={[styles.cancelButton, { backgroundColor: theme.colors.grey.light }]}
              onPress={() => cancelFriendRequest(item.userId)}
            >
              <Text style={[styles.cancelButtonText, { color: theme.colors.text }]}>Cancelar</Text>
            </TouchableOpacity>
          )}
        </View>
      </Card>
    );
  };

  // Función para refrescar la lista de amigos
  const onRefreshFriends = async () => {
    setRefreshingFriends(true);
    await fetchFriends();
    setRefreshingFriends(false);
  };

  // Función para refrescar la lista de solicitudes
  const onRefreshRequests = async () => {
    setRefreshingRequests(true);
    await fetchFriendRequests();
    setRefreshingRequests(false);
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.title, { color: theme.colors.text }]}>Amigos</Text>
        
        <TouchableOpacity
          style={styles.addButton}
          onPress={() => setSearchModalVisible(true)}
        >
          <Ionicons name="person-add" size={24} color={theme.colors.primary} />
          {!isSmallScreen && (
            <Text style={[styles.addButtonText, { color: theme.colors.primary }]}>Agregar</Text>
          )}
        </TouchableOpacity>
      </View>
      
      {/* Tabs */}
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[
            styles.tab,
            { borderColor: activeTab === 'friends' ? theme.colors.primary : theme.colors.grey.medium },
          ]}
          onPress={() => setActiveTab('friends')}
        >
          <View style={styles.tabContentContainer}>
            {isSmallScreen && (
              <Ionicons 
                name="people" 
                size={16} 
                color={activeTab === 'friends' ? theme.colors.primary : theme.colors.text} 
                style={{ marginRight: isSmallScreen ? 4 : 0 }}
              />
            )}
            <Text
              style={[
                styles.tabText,
                { 
                  color: activeTab === 'friends' ? theme.colors.primary : theme.colors.text,
                  fontSize: isSmallScreen ? typography.fontSize.sm : typography.fontSize.md
                }
              ]}
            >
              {isSmallScreen ? 'Amigos' : 'Mis Amigos'}
            </Text>
          </View>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[
            styles.tab,
            { borderColor: activeTab === 'requests' ? theme.colors.primary : theme.colors.grey.medium },
          ]}
          onPress={() => setActiveTab('requests')}
        >
          <View style={styles.tabWithBadge}>
            <View style={styles.tabContentContainer}>
              {isSmallScreen && (
                <Ionicons 
                  name="mail" 
                  size={16} 
                  color={activeTab === 'requests' ? theme.colors.primary : theme.colors.text} 
                  style={{ marginRight: isSmallScreen ? 4 : 0 }}
                />
              )}
              <Text
                style={[
                  styles.tabText,
                  { 
                    color: activeTab === 'requests' ? theme.colors.primary : theme.colors.text,
                    fontSize: isSmallScreen ? typography.fontSize.sm : typography.fontSize.md
                  }
                ]}
              >
                Solicitudes
              </Text>
            </View>
            {friendRequests.filter(r => r.type === 'incoming').length > 0 && (
              <View style={[styles.badgeContainer, { backgroundColor: theme.colors.primary }]}>
                <Text style={[styles.badgeText, { color: theme.colors.white }]}>
                  {friendRequests.filter(r => r.type === 'incoming').length}
                </Text>
              </View>
            )}
          </View>
        </TouchableOpacity>
      </View>
      
      {/* Friends List */}
      {activeTab === 'friends' ? (
        friendsLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
          </View>
        ) : friends.length > 0 ? (
          <FlatList
            data={friends}
            renderItem={renderFriendItem}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.friendsList}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl
                refreshing={refreshingFriends}
                onRefresh={onRefreshFriends}
                colors={[theme.colors.primary]}
                tintColor={theme.colors.primary}
              />
            }
          />
        ) : (
          <View style={styles.emptyContainer}>
            <Ionicons name="people-outline" size={64} color={theme.colors.grey.medium} />
            <Text style={[styles.emptyText, { color: theme.colors.text }]}>No tienes amigos aún</Text>
            <Text style={[styles.emptySubtext, { color: theme.colors.grey.dark }]}>
              Busca a otros usuarios por nombre de usuario o código de amigo
            </Text>
          </View>
        )
      ) : (
        // Requests List
        requestsLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
          </View>
        ) : friendRequests.length > 0 ? (
          <FlatList
            data={friendRequests}
            renderItem={renderFriendRequestItem}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.friendsList}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl
                refreshing={refreshingRequests}
                onRefresh={onRefreshRequests}
                colors={[theme.colors.primary]}
                tintColor={theme.colors.primary}
              />
            }
          />
        ) : (
          <View style={styles.emptyContainer}>
            <Ionicons name="mail-outline" size={64} color={theme.colors.grey.medium} />
            <Text style={[styles.emptyText, { color: theme.colors.text }]}>No hay solicitudes pendientes</Text>
            <Text style={[styles.emptySubtext, { color: theme.colors.grey.dark }]}>
              Te notificaremos cuando alguien te envíe una solicitud de amistad
            </Text>
          </View>
        )
      )}
      
      {/* Search Modal */}
      <Modal
        visible={searchModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setSearchModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>Buscar Amigos</Text>
              <TouchableOpacity onPress={() => setSearchModalVisible(false)}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </TouchableOpacity>
            </View>
            
            {/* Username Search */}
            <View style={styles.searchSection}>
              <Text style={[styles.searchSectionTitle, { color: theme.colors.text }]}>Por nombre de usuario</Text>
              <View style={styles.searchInputContainer}>
                <View style={[styles.customInputContainer, { backgroundColor: theme.colors.grey.light, borderColor: theme.colors.border }]}>
                  <TextInput
                    style={[styles.customInput, { color: theme.colors.text }]}
                    placeholder="Buscar por nombre de usuario"
                    placeholderTextColor={theme.colors.grey.medium}
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    onSubmitEditing={searchUsersByUsername}
                    returnKeyType="search"
                  />
                </View>
                <Button
                  title="Buscar"
                  onPress={searchUsersByUsername}
                  type="primary"
                  size="small"
                  loading={searchLoading}
                  disabled={searchLoading || !searchQuery.trim()}
                  style={styles.searchButton}
                />
              </View>
            </View>
            
            {/* Friend Code Search */}
            <View style={styles.searchSection}>
              <Text style={[styles.searchSectionTitle, { color: theme.colors.text }]}>Por código de amigo</Text>
              <View style={styles.searchInputContainer}>
                <View style={[styles.customInputContainer, { backgroundColor: theme.colors.grey.light, borderColor: theme.colors.border }]}>
                  <TextInput
                    style={[styles.customInput, { color: theme.colors.text }]}
                    placeholder="Código de amigo"
                    placeholderTextColor={theme.colors.grey.medium}
                    value={friendCode}
                    onChangeText={setFriendCode}
                    onSubmitEditing={searchUserByFriendCode}
                    returnKeyType="search"
                  />
                </View>
                <Button
                  title="Buscar"
                  onPress={searchUserByFriendCode}
                  type="primary"
                  size="small"
                  loading={searchLoading}
                  disabled={searchLoading || !friendCode.trim()}
                  style={styles.searchButton}
                />
              </View>
            </View>
            
            {/* Search Results */}
            {searchLoading ? (
              <View style={styles.searchLoadingContainer}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
              </View>
            ) : searchResults.length > 0 ? (
              <FlatList
                data={searchResults}
                renderItem={renderSearchResultItem}
                keyExtractor={(item) => item.id}
                contentContainerStyle={styles.searchResultsList}
                showsVerticalScrollIndicator={false}
              />
            ) : (
              <View style={styles.searchEmptyContainer}>
                <Text style={[styles.searchEmptyText, { color: theme.colors.grey.dark }]}>
                  No se encontraron resultados
                </Text>
              </View>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: spacing.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  addButtonText: {
    marginLeft: spacing.xs,
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  emptyText: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    marginTop: spacing.md,
  },
  emptySubtext: {
    fontSize: typography.fontSize.md,
    textAlign: 'center',
    marginTop: spacing.sm,
  },
  friendsList: {
    paddingBottom: spacing.lg,
  },
  friendCard: {
    marginBottom: spacing.md,
  },
  friendHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  friendInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarContainer: {
    marginRight: spacing.md,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  avatarText: {
    color: colors.white,
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
  },
  friendName: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
  },
  friendUsername: {
    fontSize: typography.fontSize.sm,
  },
  removeButton: {
    padding: spacing.sm,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: borderRadius.lg,
    borderTopRightRadius: borderRadius.lg,
    padding: spacing.lg,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  modalTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  searchSection: {
    marginBottom: spacing.lg,
  },
  searchSectionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
    marginBottom: spacing.sm,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchInput: {
    flex: 1,
    marginRight: spacing.sm,
    marginBottom: 0,
  },
  customInputContainer: {
    flex: 1,
    marginRight: spacing.sm,
    borderWidth: 1,
    borderRadius: borderRadius.md,
    height: 48,
    justifyContent: 'center',
  },
  customInput: {
    paddingHorizontal: spacing.md,
    fontSize: typography.fontSize.md,
    height: '100%',
  },
  searchButton: {
    height: 48,
    justifyContent: 'center',
  },
  searchLoadingContainer: {
    padding: spacing.xl,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchResultsList: {
    paddingBottom: spacing.lg,
  },
  searchResultCard: {
    marginBottom: spacing.sm,
  },
  searchEmptyContainer: {
    padding: spacing.xl,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchEmptyText: {
    fontSize: typography.fontSize.md,
  },
  tabContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  tab: {
    flex: 1,
    padding: spacing.md,
    borderWidth: 1,
    borderRadius: borderRadius.sm,
    marginHorizontal: spacing.xs,
  },
  activeTab: {
    borderColor: colors.primary,
  },
  tabText: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
  },
  activeTabText: {
    color: colors.primary,
  },
  tabWithBadge: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  badgeContainer: {
    borderRadius: borderRadius.sm,
    padding: spacing.xs,
    marginLeft: spacing.xs,
  },
  badgeText: {
    fontSize: typography.fontSize.sm,
    fontWeight: 'bold',
  },
  requestActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  requestButton: {
    padding: spacing.sm,
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: spacing.xs,
  },
  acceptButton: {
    backgroundColor: colors.success,
  },
  rejectButton: {
    backgroundColor: colors.error,
  },
  cancelButton: {
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
  },
  cancelButtonText: {
    fontSize: typography.fontSize.sm,
    fontWeight: 'bold',
  },
  requestStatusText: {
    fontSize: typography.fontSize.xs,
    marginTop: spacing.xs,
  },
  friendItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
    padding: spacing.md,
  },
  friendImageContainer: {
    marginRight: spacing.md,
  },
  friendImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  friendImagePlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  friendInitials: {
    color: colors.white,
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
  },
  friendDetails: {
    flexDirection: 'column',
  },
  friendActions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: spacing.md,
  },
  friendAction: {
    padding: spacing.md,
    borderRadius: 50,
    backgroundColor: `${colors.grey.light}40`,
  },
  tabContentContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default FriendManagementScreen; 